import React, { useState, useEffect, useRef } from 'react';
import {
  Drawer,
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Divider,
  Alert
} from '@mui/material';
import bugReportService, { BugReport } from '../../services/bugReportService';

interface AddBugReportFormProps {
  open: boolean;
  onClose: () => void;
  onAdd: () => void;
  onDelete?: (id: number) => void; // Callback do optymistycznego usuwania
  editData?: BugReport | null;
}

interface FormData {
  title: string;
  description: string;
  screenshots: string[];
}

const AddBugReportForm: React.FC<AddBugReportFormProps> = ({ 
  open, 
  onClose, 
  onAdd,
  onDelete,
  editData
}) => {
  const isEditMode = !!editData;
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    screenshots: []
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pasteAreaRef = useRef<HTMLDivElement>(null);

  // Reset formularza przy zamknięciu lub wypełnij danymi edycji
  useEffect(() => {
    if (!open) {
      setFormData({
        title: '',
        description: '',
        screenshots: []
      });
      setError(null);
      setLoading(false); // Reset loading przy zamknięciu
    } else if (editData) {
      setFormData({
        title: editData.title,
        description: editData.description,
        screenshots: editData.screenshots || []
      });
      setLoading(false); // Reset loading przy otwarciu w trybie edycji
    }
  }, [open, editData]);

  // Obsługa paste dla screenshotów
  useEffect(() => {
    if (!open) return;

    const handlePaste = async (e: Event) => {
      const clipboardEvent = e as ClipboardEvent;
      try {
        const items = clipboardEvent.clipboardData?.items || [];
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.kind === 'file') {
            const file = item.getAsFile();
            if (file && file.type.startsWith('image/')) {
              clipboardEvent.preventDefault();
              await handleImageUpload(file);
              return;
            }
          }
        }
      } catch (err) {
        console.error('Błąd podczas wklejania obrazu:', err);
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => {
      window.removeEventListener('paste', handlePaste);
    };
  }, [open]);

  const handleImageUpload = async (file: File) => {
    try {
      setUploadingImages(true);
      const url = await bugReportService.uploadScreenshot(file);
      setFormData(prev => ({
        ...prev,
        screenshots: [...prev.screenshots, url]
      }));
    } catch (err: any) {
      console.error('Błąd podczas przesyłania obrazu:', err);
      setError('Nie udało się przesłać obrazu');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      Array.from(files).forEach(file => {
        if (file.type.startsWith('image/')) {
          handleImageUpload(file);
        }
      });
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveScreenshot = (index: number) => {
    setFormData(prev => ({
      ...prev,
      screenshots: prev.screenshots.filter((_, i) => i !== index)
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Walidacja
      if (!formData.title.trim()) {
        setError('Tytuł jest wymagany');
        setLoading(false);
        return;
      }

      if (!formData.description.trim()) {
        setError('Opis jest wymagany');
        setLoading(false);
        return;
      }

      if (isEditMode && editData) {
        // Aktualizacja istniejącego zgłoszenia
        await bugReportService.updateBugReport(editData.id, {
          title: formData.title,
          description: formData.description,
          screenshots: formData.screenshots
        });
      } else {
        // Tworzenie nowego zgłoszenia
        await bugReportService.createBugReport({
          title: formData.title,
          description: formData.description,
          screenshots: formData.screenshots
        });
      }

      onAdd();
      onClose();
    } catch (err: any) {
      console.error('Błąd podczas zgłaszania błędu:', err);
      setError(err.response?.data?.message || err.message || 'Wystąpił błąd podczas zgłaszania błędu');
    } finally {
      setLoading(false);
    }
  };

  // Style zgodne z dokumentacją
  const textFieldSx = {
    '& .MuiInputLabel-root': {
      fontFamily: 'Roboto, sans-serif',
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '16px',
      color: '#2D2D32'
    },
    '& .MuiOutlinedInput-root': {
      height: 32,
      backgroundColor: '#fff',
      borderRadius: '4px'
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '& .MuiOutlinedInput-root.Mui-error:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiInputBase-input': {
      padding: '8px 12px',
      boxSizing: 'border-box',
      fontFamily: 'Roboto, sans-serif',
      fontSize: '16px',
      color: '#2D2D32'
    },
    '& .MuiFormHelperText-root': { ml: 0 }
  } as const;

  const FieldLabel: React.FC<{ text: string; required?: boolean }> = ({ text, required }) => (
    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#2D2D32', mb: 0.5 }}>
      {text}
      {required && <Box component="span" sx={{ color: '#d32f2f' }}> *</Box>}
    </Typography>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 680, maxWidth: '100vw', borderRadius: 0 } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Nagłówek */}
        <Box sx={{ pl: 4, pr: 4, pt: '22px', pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>
            {isEditMode ? 'Edytuj błąd' : 'Dodaj błąd'}
          </Typography>
          <IconButton 
            onClick={onClose} 
            size="small"
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 30,
              minHeight: 30,
              '& > *': {
                pointerEvents: 'none',
              },
            }}
          >
            <Box 
              component="img" 
              src="/assets/close.svg" 
              alt="Zamknij" 
              sx={{ 
                width: 13, 
                height: 13, 
                display: 'block',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }} 
            />
          </IconButton>
        </Box>

        {/* Zawartość formularza */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 4, py: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container rowSpacing={1.5} columnSpacing={3}>
            {/* Tytuł */}
            <Grid item xs={12}>
              <FieldLabel text="Tytuł" required />
              <TextField
                fullWidth
                name="title"
                value={formData.title}
                onChange={handleChange}
                variant="outlined"
                size="small"
                sx={textFieldSx}
                placeholder="Nie działa dodawanie nowego projektu na stronie https://app.unitresco.com/offers"
                error={!!error && !formData.title.trim()}
                helperText={error && !formData.title.trim() ? 'To pole jest wymagane' : ''}
              />
            </Grid>

            {/* Opis */}
            <Grid item xs={12}>
              <FieldLabel text="Opis" required />
              <TextField
                fullWidth
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={4}
                variant="outlined"
                size="small"
                sx={{ 
                  ...textFieldSx, 
                  '& .MuiOutlinedInput-root': { 
                    height: 'auto', 
                    backgroundColor: '#fff', 
                    borderRadius: '4px', 
                    alignItems: 'flex-start' 
                  },
                  '& .MuiInputBase-input': { 
                    padding: 0 
                  },
                  '& .MuiOutlinedInput-input': { 
                    padding: 0 
                  }
                }}
                placeholder="Szczegółowy opis błędu, kroki do odtworzenia itp."
                error={!!error && !formData.description.trim()}
                helperText={error && !formData.description.trim() ? 'To pole jest wymagane' : ''}
              />
            </Grid>

            {/* Screeny */}
            <Grid item xs={12}>
              <FieldLabel text="Screeny" />
              <Box
                ref={pasteAreaRef}
                sx={{
                  border: '2px dashed #d4d4d9',
                  borderRadius: '4px',
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: '#FAFAFA',
                  '&:hover': {
                    borderColor: '#595966',
                    backgroundColor: '#F5F5F5'
                  }
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileInputChange}
                />
                <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#595966', mb: 1 }}>
                  Kliknij, aby dodać zdjęcie lub wklej obraz ze schowka (Ctrl+V / Cmd+V)
                </Typography>
                {uploadingImages && (
                  <CircularProgress size={24} sx={{ mt: 1 }} />
                )}
              </Box>

              {/* Lista dodanych screenshotów */}
              {formData.screenshots.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {formData.screenshots.map((url, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: 'relative',
                        width: 120,
                        height: 120,
                        borderRadius: '4px',
                        overflow: 'hidden',
                        border: '1px solid #d4d4d9'
                      }}
                    >
                      <Box
                        component="img"
                        src={url}
                        alt={`Screenshot ${index + 1}`}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveScreenshot(index)}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          color: '#fff',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.7)'
                          },
                          width: 24,
                          height: 24
                        }}
                      >
                        <Box component="img" src="/assets/close.svg" alt="Usuń" sx={{ width: 12, height: 12, filter: 'invert(1)' }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>

        {/* Stopka z przyciskami */}
        <Divider />
        <Box sx={{ px: 4, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button 
            onClick={async () => {
              if (isEditMode && editData?.id) {
                // Tryb edycji - usuń błąd
                if (window.confirm('Czy na pewno chcesz usunąć to zgłoszenie błędu?')) {
                  const idToDelete = editData.id;
                  try {
                    setLoading(true);
                    // Optymistyczne usunięcie - natychmiast usuń z listy
                    if (onDelete) {
                      onDelete(idToDelete);
                    }
                    // Zamknij formularz natychmiast
                    onClose();
                    setLoading(false); // Reset loading przed zamknięciem
                    // Usuń z API w tle
                    await bugReportService.deleteBugReport(idToDelete);
                    // Odśwież listę aby upewnić się, że wszystko jest zsynchronizowane
                    onAdd();
                  } catch (err: any) {
                    console.error('Błąd podczas usuwania błędu:', err);
                    setLoading(false);
                    // Jeśli błąd, odśwież listę aby przywrócić element
                    onAdd();
                    // Nie otwieraj formularza ponownie - tylko pokaż błąd jeśli formularz jest otwarty
                    if (open) {
                      setError(err.response?.data?.message || err.message || 'Wystąpił błąd podczas usuwania błędu');
                    }
                  }
                }
              } else {
                // Tryb dodawania - wyczyść formularz
                setFormData({
                  title: '',
                  description: '',
                  screenshots: []
                });
                setError(null);
              }
            }}
            variant="text" 
            disabled={loading}
            sx={{ 
              fontFamily: 'Roboto, sans-serif', 
              fontWeight: 500, 
              fontSize: 14, 
              color: '#d32f2f', 
              textTransform: 'none' 
            }}
          >
            Usuń
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              onClick={onClose} 
              variant="text" 
              disabled={loading}
              sx={{ 
                fontFamily: 'Roboto, sans-serif', 
                fontWeight: 500, 
                fontSize: 14, 
                color: '#2D2D32', 
                textTransform: 'none' 
              }}
            >
              Anuluj
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              disabled={loading}
              sx={{ 
                backgroundColor: '#020242',
                '&:hover': {
                  backgroundColor: '#030360'
                }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : (isEditMode ? 'Zapisz zmiany' : 'Dodaj błąd')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AddBugReportForm;

